/**
 * Validation des changements de comportement annoncés par l'IA (§5.1).
 *
 * La réécriture applique des règles de plateforme — pas de stockage dans le
 * navigateur, pas de `alert()`, pas d'appel direct au modèle — et certaines
 * changent ce que l'application fait. Jusqu'ici l'auteur le découvrait en
 * production, sur une application qui n'était plus tout à fait la sienne.
 *
 * Le déploiement est donc **suspendu** : rien n'est construit, rien n'est mis en
 * ligne, et l'application déjà en service continue de tourner sans être touchée.
 * Ce panneau porte la décision.
 *
 * Deux partis pris d'affichage :
 *
 * - **aucune action par défaut.** Ni bouton présélectionné, ni délai au bout
 *   duquel on poursuivrait : un consentement obtenu par lassitude n'en est pas un ;
 * - **le refus est aussi lisible que l'acceptation.** Un bouton d'abandon relégué
 *   en petits caractères revient à ne pas offrir le choix.
 *
 * Une **troisième voie** s'est imposée à l'usage : accepter ou tout abandonner
 * est un choix pauvre, parce que les changements annoncés sont souvent *presque*
 * bons. « L'objectif de la jauge, c'est 15, pas 20 » se dit en une phrase — et il
 * fallait, pour le corriger, abandonner puis recoller un code repris à la main.
 * L'auteur peut désormais répondre à l'IA, qui reprend son travail avec la
 * remarque en plus. Le numéro de version ne bouge pas : rien n'a été construit.
 */

import { MAX_MODIFICATION_INSTRUCTION_LENGTH, type FunctionalChangeDto } from '@portail/shared';
import clsx from 'clsx';
import { AlertTriangle, Ban, MessageSquarePlus, Rocket, Send } from 'lucide-react';
import { useId, useState } from 'react';

import { formatNumber } from '../lib/format';
import { Button, Card, ErrorBanner, FOCUS_RING, Label } from './ui';

/** Longueur minimale d'une précision, alignée sur le contrôle du serveur. */
const MIN_PRECISIONS = 10;

/**
 * Nature du déploiement, qui décide de ce que la liste **signifie**.
 *
 * Le titre annonçait « votre application ne fonctionnera pas exactement comme
 * avant » dans les trois cas. C'est juste pour une conversion — l'artifact
 * existait et se comportait d'une certaine façon — et faux pour une création :
 * il n'y a pas d'avant. L'auteur qui déploie pour la première fois une
 * application écrite d'après sa description lisait donc un avertissement sur une
 * régression imaginaire, là où la liste décrit en réalité les décisions prises
 * en l'absence de consigne.
 */
export type DeploymentKind =
  /** Application écrite d'après une description : rien n'existait avant. */
  | 'creation'
  /** Artifact collé, première mise en ligne : il avait déjà un comportement. */
  | 'conversion'
  /** Nouvelle version d'une application déjà en service. */
  | 'update';

export interface FunctionalChangesPanelProps {
  changes: FunctionalChangeDto[];
  /** Nom de l'application, pour ancrer la décision sur un objet concret. */
  slug: string;
  /** Numéro de la version en préparation, s'il est connu. */
  versionNumber?: number | null;
  kind: DeploymentKind;
  /** Vrai lorsque l'application est déjà en ligne : le risque n'est pas le même. */
  live: boolean;
  busy: 'accept' | 'refuse' | 'refine' | null;
  error: string | null;
  onAccept: () => void;
  onRefuse: () => void;
  /** Renvoie des précisions à l'IA, qui reprend son travail sans changer de version. */
  onRefine: (precisions: string) => void;
  /** Nombre d'échanges déjà eus, pour que l'auteur sache où il en est. */
  refinements?: number;
}

/** Liste des changements annoncés et boutons de décision. */
export function FunctionalChangesPanel({
  changes,
  slug,
  versionNumber,
  kind,
  live,
  busy,
  error,
  onAccept,
  onRefuse,
  onRefine,
  refinements = 0,
}: FunctionalChangesPanelProps) {
  const plural = changes.length > 1;
  const [ouvert, setOuvert] = useState(false);
  const [precisions, setPrecisions] = useState('');
  const precisionsId = useId();
  const assezPrecis = precisions.trim().length >= MIN_PRECISIONS;

  return (
    <Card className="border-amber-300 p-6 dark:border-amber-900">
      <div className="flex items-start gap-4">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950"
          aria-hidden="true"
        >
          <AlertTriangle className="h-6 w-6 text-amber-700 dark:text-amber-400" />
        </span>

        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
            {kind === 'creation'
              ? plural
                ? 'Claude a tranché quelques points à votre place'
                : 'Claude a tranché un point à votre place'
              : plural
                ? 'Votre application ne fonctionnera pas exactement comme avant'
                : 'Un détail du fonctionnement de votre application va changer'}
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {kind === 'creation' ? (
              <>
                «&nbsp;{slug}&nbsp;» n'existait pas encore : votre description ne pouvait pas tout
                préciser, et {plural ? 'ces points ont' : 'ce point a'} été décidé
                {plural ? 's' : ''} pour vous. Rien n'a encore été construit ni mis en ligne — dites
                ce qui ne vous convient pas et Claude reprendra son travail.
              </>
            ) : (
              <>
                Pour être hébergée sur nos serveurs, «&nbsp;{slug}&nbsp;» a dû être adaptée. La
                plupart des modifications sont invisibles, mais {plural ? 'celles-ci' : 'celle-ci'}{' '}
                se{plural ? 'ront' : 'ra'} remarqué{plural ? 'es' : 'e'} à l'usage. Rien n'a encore
                été construit ni mis en ligne.
              </>
            )}
          </p>

          <ul className="mt-4 space-y-3">
            {changes.map((change, index) => (
              <li
                key={index}
                className="rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950"
              >
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {change.change}
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{change.reason}</p>
              </li>
            ))}
          </ul>

          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            {live
              ? 'Si vous abandonnez, la version actuellement en ligne reste en place, intacte.'
              : "Si vous abandonnez, rien n'est mis en ligne et le nom reste disponible."}{' '}
            {kind === 'creation'
              ? 'Vous pourrez reprendre votre description et redéployer autant de fois que nécessaire.'
              : 'Vous pourrez corriger votre code et redéployer autant de fois que nécessaire.'}
          </p>

          {error ? (
            <div className="mt-4">
              <ErrorBanner message={error} />
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              onClick={onAccept}
              loading={busy === 'accept'}
              disabled={busy !== null}
            >
              <Rocket className="h-4 w-4" aria-hidden="true" />
              Continuer et mettre en ligne
              {typeof versionNumber === 'number' ? ` la v${versionNumber}` : null}
            </Button>
            {/*
              La troisième voie est présentée **avant** l'abandon : c'est presque
              toujours la bonne réponse quand un point annoncé ne convient pas, et
              la mettre en dernier laisserait croire qu'abandonner est le seul
              recours.
            */}
            <Button onClick={() => setOuvert((o) => !o)} disabled={busy !== null}>
              <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
              Demander une correction
            </Button>
            <Button
              variant="secondary"
              onClick={onRefuse}
              loading={busy === 'refuse'}
              disabled={busy !== null}
            >
              <Ban className="h-4 w-4" aria-hidden="true" />
              Abandonner ce déploiement
            </Button>
          </div>

          {ouvert ? (
            <div className="mt-4 rounded-md border border-indigo-300 bg-indigo-50/50 p-4 dark:border-indigo-800 dark:bg-indigo-950/40">
              <Label
                htmlFor={precisionsId}
                required
                hint={`${formatNumber(precisions.length)} / ${formatNumber(MAX_MODIFICATION_INSTRUCTION_LENGTH)}`}
              >
                Ce que Claude doit corriger
              </Label>
              <textarea
                id={precisionsId}
                value={precisions}
                rows={4}
                maxLength={MAX_MODIFICATION_INSTRUCTION_LENGTH}
                autoFocus
                onChange={(event) => setPrecisions(event.target.value)}
                placeholder="Répondez comme à un collègue : « L’objectif de la jauge, c’est 15, pas 20. Et ajoute un vrai champ « date de clôture » plutôt que d’utiliser la date de mise à jour. »"
                className={clsx(
                  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm',
                  'text-slate-900 placeholder:text-slate-400',
                  'dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
                  FOCUS_RING,
                )}
              />
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                Claude reprend son travail avec votre remarque, puis vous représente ce qui change.
                Rien n'est mis en ligne d'ici là, et
                {typeof versionNumber === 'number'
                  ? ` le numéro de version reste v${versionNumber}`
                  : ' le numéro de version ne bouge pas'}{' '}
                : autant d'échanges que nécessaire.
                {refinements > 0
                  ? ` ${refinements} remarque${refinements > 1 ? 's' : ''} déjà transmise${refinements > 1 ? 's' : ''}, et Claude les conserve toutes.`
                  : ''}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  onClick={() => onRefine(precisions.trim())}
                  loading={busy === 'refine'}
                  disabled={busy !== null || !assezPrecis}
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Renvoyer à Claude
                </Button>
                <Button variant="ghost" onClick={() => setOuvert(false)} disabled={busy !== null}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
